import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccessToken } from '../../../slices/staffSlice';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { show } from '../../../slices/toastSlice';
import { Topbar, Sidebar, Loader } from '../../../components';
import { HugeiconsIcon } from '@hugeicons/react';
import { Key01Icon, FileAddIcon, CheckmarkCircle01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';
import Create from './Create';
import ApiKeyModal from './ApiKeyModal';
import Toggle from './Toggle';

function Index() {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();

    const [partners, setPartners] = useState([]);
    const [loader, setLoader] = useState(true);

    // create modal
    const [createOpen, setCreateOpen] = useState(false);

    // api key modal
    const [apiKeyOpen, setApiKeyOpen] = useState(false);
    const [newApiKey, setNewApiKey] = useState('');

    // toggle modal
    const [toggleOpen, setToggleOpen] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);

    // regenerate key
    const [regenProcessing, setRegenProcessing] = useState(null);

    const loadPartners = async () => {
        const headers = authStaff(accessToken);
        const res = await fetchRequests('/loans/staff/partner-apis', 'GET', headers, {}, {});
        if (!res?.err) {
            setPartners(res?.partners || []);
        } else {
            dispatch(show({ type: 'error', state: true, message: res?.err }));
        }
        setLoader(false);
    };

    useEffect(() => {
        document.title = `Partner APIs - ${process.env.REACT_APP_SITE_NAME}`;
        loadPartners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreated = (apiKey) => {
        setCreateOpen(false);
        setNewApiKey(apiKey);
        setApiKeyOpen(true);
    };

    const handleApiKeyClose = () => {
        setApiKeyOpen(false);
        setNewApiKey('');
        loadPartners();
    };

    const handleToggleClick = (partner) => {
        setSelectedPartner(partner);
        setToggleOpen(true);
    };

    const handleToggled = () => {
        setToggleOpen(false);
        setSelectedPartner(null);
        loadPartners();
    };

    const handleRegenerate = async (partner) => {
        if (!window.confirm(`Regenerate API key for ${partner.name}? The current key will be permanently invalidated.`)) return;
        setRegenProcessing(partner._id);
        const headers = authStaff(accessToken);
        const res = await fetchRequests('/loans/staff/partner-apis/regenerate-key', 'PUT', headers, {}, { _id: partner._id });
        if (res?.err) {
            dispatch(show({ type: 'error', state: true, message: res.err }));
        } else if (res?.apiKey) {
            setNewApiKey(res.apiKey);
            setApiKeyOpen(true);
            dispatch(show({ type: 'success', state: true, message: res?.message || 'API key regenerated' }));
        }
        setRegenProcessing(null);
    };

    return (
        <>
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-6">

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                                <HugeiconsIcon icon={Key01Icon} size={18} className="text-[#1e3a6e]" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Partner APIs</h1>
                                    {partners.length > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full">
                                            {partners.length}
                                        </span>
                                    )}
                                    {loader && <Loader color="text-gray-400" size="w-4 h-4" />}
                                </div>
                                <p className="text-sm text-gray-400">Manage API keys for pension trustee partners</p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => setCreateOpen(true)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#264d91] transition-colors shrink-0"
                        >
                            <HugeiconsIcon icon={FileAddIcon} size={15} />
                            Create Partner API
                        </button>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-gray-50/60 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-700">
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Name</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Code</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">IP Whitelist</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Contact</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Created</th>
                                    <th className="px-5 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                {partners.length > 0 ? (
                                    partners.map((partner) => (
                                        <tr key={partner._id} className="hover:bg-gray-50/40 dark:hover:bg-gray-700/30 transition-colors">
                                            <td className="px-5 py-3.5 text-sm font-medium text-gray-800 dark:text-gray-100">
                                                {partner.name}
                                            </td>
                                            <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400 font-mono">
                                                {partner.code}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full ${
                                                    partner.status === 'active'
                                                        ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20'
                                                        : 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-500/20'
                                                }`}>
                                                    <HugeiconsIcon icon={partner.status === 'active' ? CheckmarkCircle01Icon : Cancel01Icon} size={10} />
                                                    {partner.status}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400">
                                                {partner.ipWhitelist?.length > 0
                                                    ? <span className="font-mono text-xs">{partner.ipWhitelist.join(', ')}</span>
                                                    : <span className="text-xs text-gray-400">All IPs</span>
                                                }
                                            </td>
                                            <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400">
                                                {partner.contactEmail}
                                            </td>
                                            <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400">
                                                {new Date(partner.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-5 py-3.5 text-right space-x-1">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRegenerate(partner)}
                                                    disabled={regenProcessing === partner._id}
                                                    className="text-xs font-medium px-3 py-1.5 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10 transition-colors disabled:opacity-50"
                                                >
                                                    {regenProcessing === partner._id ? 'Regenerating...' : 'Regenerate Key'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleClick(partner)}
                                                    className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                                                        partner.status === 'active'
                                                            ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10'
                                                            : 'text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-500/10'
                                                    }`}
                                                >
                                                    {partner.status === 'active' ? 'Disable' : 'Enable'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="px-5 py-12 text-center text-sm text-gray-400">
                                            {loader ? 'Loading...' : 'No partner APIs created yet'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                </section>
            </main>
        </div>

        <Create isOpen={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
        <ApiKeyModal isOpen={apiKeyOpen} onClose={handleApiKeyClose} apiKey={newApiKey} />
        <Toggle isOpen={toggleOpen} onClose={() => { setToggleOpen(false); setSelectedPartner(null); }} partner={selectedPartner} onToggled={handleToggled} />
        </>
    );
}

export default Index;
